import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { MobileCategoryDrawer } from "@/components/edition/category";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { cn } from "@/lib/utils";
import { Typography } from "@bookips/sds";
import { memo, useCallback, useState } from "react";
import { convertToCategories, parseCategoryId } from "../utils/ipSelectUtils";
import { useIpSelect } from "./hooks/useIpSelect";
import IpSelectFilter from "./IpSelectFilter";
import SelectedBookCard from "./SelectedBookCard";
/**
 * IP Select 섹션 컴포넌트
 * 계층형 필터 (학년 → 카테고리 → 시리즈 → 교재) + 선택된 교재 카드
 */ export const IpSelectSection = /*#__PURE__*/ memo((param)=>{
    let { data , selectedBook , onSelectSource , isLoading , isDefaultSelected , homeId , className  } = param;
    const { depthLabels , items  } = data;
    const { selectedIndices , selectedItems , optionsByDepth , openDepth , visibleDepthCount , handleSelect , handleToggleDropdown , handleCloseDropdown  } = useIpSelect({
        items,
        depthLabels,
        onSelect: onSelectSource
    });
    // MX-378: 뎁스명 클릭 트래킹 래퍼
    const handleToggleWithTracking = useCallback((depth)=>{
        mixpanelEvent("[Home] IP Select Section Clicked", {
            "Home Id": homeId,
            Type: "뎁스명 클릭",
            "Depth Title": depthLabels[depth]
        });
        handleToggleDropdown(depth);
    }, [
        depthLabels,
        handleToggleDropdown
    ]);
    // MX-382, MX-383: 아이템 선택 트래킹 래퍼
    const handleSelectWithTracking = useCallback((depth, index)=>{
        var _optionsByDepth_depth;
        const item = (_optionsByDepth_depth = optionsByDepth[depth]) === null || _optionsByDepth_depth === void 0 ? void 0 : _optionsByDepth_depth[index];
        // MX-382: 뎁스 내 아이템 클릭 트래킹
        mixpanelEvent("[Home] IP Select Section Clicked", {
            "Home Id": homeId,
            Type: "뎁스 내 아이템 클릭",
            "Depth Title": depthLabels[depth],
            "Item Name": item === null || item === void 0 ? void 0 : item.label
        });
        // MX-383: leaf 노드 선택 시 Source Updated 트래킹
        if (item === null || item === void 0 ? void 0 : item.sourceId) {
            mixpanelEvent("[Home] IP Select Section Updated", {
                "Home Id": homeId,
                "Source Id": item.sourceId,
                "Source Title": item.label
            });
        }
        handleSelect(depth, index);
    }, [
        depthLabels,
        optionsByDepth,
        handleSelect
    ]);
    // 모바일 Drawer 상태
    const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
    // IpSelectItem → CategoryItem 변환
    const categoryItems = convertToCategories(items, depthLabels, selectedIndices);
    // 모바일 Drawer에서 열려있어야 할 섹션 (마지막 선택된 depth만)
    const lastSelectedDepth = selectedIndices.reduce((last, idx, depth)=>idx !== undefined ? depth : last, 0);
    const defaultExpandedSections = [
        "depth-".concat(lastSelectedDepth)
    ];
    // 선택된 아이템의 categoryId (하이라이트용)
    const selectedCategoryIds = selectedIndices.map((index, depth)=>index !== undefined ? "".concat(depth, "-").concat(index) : null);
    // 모바일 Drawer 열기/닫기
    const handleOpenMobileDrawer = ()=>setIsMobileDrawerOpen(true);
    const handleCloseMobileDrawer = ()=>setIsMobileDrawerOpen(false);
    // 모바일 Drawer에서 카테고리 선택 시
    const handleCategorySelect = useCallback((categoryId)=>{
        const parsed = parseCategoryId(categoryId);
        if (!parsed) return;
        const { depth , index  } = parsed;
        handleSelectWithTracking(depth, index);
        // 마지막 depth (leaf 노드) 선택 시에만 drawer 닫기
        const isLastDepth = depth === depthLabels.length - 1;
        if (isLastDepth) {
            setIsMobileDrawerOpen(false);
        }
    }, [
        handleSelectWithTracking,
        depthLabels.length
    ]);
    // 툴팁 표시 조건: 기본 교재 선택 상태 + 드롭다운 닫혀있을 때
    const showTooltip = isDefaultSelected && openDepth === null;
    return /*#__PURE__*/ _jsxs("section", {
        className: cn("flex w-full flex-col gap-5 max-sm:gap-3", className),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex w-full max-w-[1200px] items-center gap-5",
                children: /*#__PURE__*/ _jsx(Typography, {
                    variant: "h4",
                    className: "text-strong max-sm:typo-h6",
                    children: "\uD83C\uDFF7️ 선택한 교재의 자료를 보여드려요."
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-3 max-sm:hidden",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex items-center gap-2",
                        children: depthLabels === null || depthLabels === void 0 ? void 0 : depthLabels.map((label, depth)=>{
                            const options = optionsByDepth[depth] || [];
                            const selectedIndex = selectedIndices[depth];
                            const selectedItem = selectedItems[depth];
                            // 이전 depth가 선택되어야 현재 depth 활성화
                            const isDisabled = depth > 0 && selectedIndices[depth - 1] === undefined;
                            return /*#__PURE__*/ _jsx(IpSelectFilter, {
                                label: label,
                                selectedLabel: selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.label,
                                options: options,
                                isOpen: openDepth === depth,
                                selectedIndex: selectedIndex,
                                onToggle: ()=>handleToggleWithTracking(depth),
                                onSelect: (index)=>handleSelectWithTracking(depth, index),
                                onClose: handleCloseDropdown,
                                disabled: isDisabled
                            }, "".concat(label, "-").concat(depth));
                        })
                    }),
                    showTooltip && /*#__PURE__*/ _jsxs("div", {
                        className: "hidden items-center rounded-md min-[1080px]:flex",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex h-8 w-2 items-center justify-center",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "h-0 w-0 border-t-[6px] border-r-[8px] border-b-[6px] border-t-transparent border-r-gray-700 border-b-transparent"
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "rounded-md bg-gray-700 px-3 py-1.5 shadow-md",
                                children: /*#__PURE__*/ _jsx(Typography, {
                                    variant: "subtitle1",
                                    className: "whitespace-nowrap text-white",
                                    children: "다른 교재로 변경해보세요."
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SelectedBookCard, {
                book: selectedBook,
                isLoading: isLoading,
                onOpenDrawer: handleOpenMobileDrawer
            }),
            /*#__PURE__*/ _jsx(MobileCategoryDrawer, {
                isOpen: isMobileDrawerOpen,
                onClose: handleCloseMobileDrawer,
                title: "교재 바꾸기",
                items: categoryItems,
                onCategorySelect: handleCategorySelect,
                defaultExpandedItems: defaultExpandedSections,
                closeOnSelect: false,
                selectedCategoryIds: selectedCategoryIds
            })
        ]
    });
});
IpSelectSection.displayName = "IpSelectSection";
export default IpSelectSection;
